/**
 * Costanti runtime del Support Hub (etichette, varianti badge, transizioni,
 * vincoli allegati). I tipi stanno in `shared/types/support.ts`, che resta
 * type-only: qui vivono i valori usati a runtime dal frontend.
 */

export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';

/** Stati del ticket, nell'ordine in cui compaiono nelle select dei filtri. */
export const TICKET_STATUSES = [
  'aperto',
  'preso_in_carico',
  'in_lavorazione',
  'in_attesa_associato',
  'risolto',
  'chiuso',
] as const;

/**
 * Valore speciale del filtro stato (backend: `buildStatusFilter` in
 * `services/supportTicketService.js`): tutti i ticket non risolti/chiusi.
 * E il default della coda operatore.
 */
export const STATUS_ACTIVE = 'active';
export const STATUS_ACTIVE_LABEL = 'Attivi (non risolti/chiusi)';

/** Conteggio "attivi" con fallback su `open` (backend precedenti al campo `active`). */
export const activeCount = (s: { active?: number; open: number }): number =>
  typeof s.active === 'number' ? s.active : s.open;

/** Conteggio "da leggere": assente sui backend precedenti. */
export const unreadCount = (s: { unread?: number }): number =>
  typeof s.unread === 'number' ? s.unread : 0;

export const TICKET_PRIORITIES = ['bassa', 'media', 'alta', 'urgente'] as const;

/** Canali d'ingresso: allineati all'enum `origin` di `backend/models/SupportTicket.js`. */
export const TICKET_ORIGINS = ['area_riservata', 'contact_form', 'feedback', 'email', 'admin'] as const;

export const ORIGIN_LABELS: Record<string, string> = {
  area_riservata: 'Area riservata',
  contact_form: 'Form contatti',
  feedback: 'Feedback',
  email: 'Email',
  admin: 'Inserito da operatore',
};

/** Tipi di richiedente: enum `requester.type` di `backend/models/SupportTicket.js`. */
export const REQUESTER_TYPES = ['associate', 'guest', 'user'] as const;

export const REQUESTER_TYPE_LABELS: Record<string, string> = {
  associate: 'Associato',
  guest: 'Ospite',
  user: 'Utente interno',
};

/** Etichette operatore (coda e dettaglio area associativa). */
export const STATUS_LABELS: Record<string, string> = {
  aperto: 'Aperto',
  preso_in_carico: 'Preso in carico',
  in_lavorazione: 'In lavorazione',
  in_attesa_associato: 'In attesa associato',
  risolto: 'Risolto',
  chiuso: 'Chiuso',
};

/** Etichette lato richiedente associato (maschile). */
export const REQUESTER_STATUS_LABELS: Record<string, string> = {
  aperto: 'Aperto',
  preso_in_carico: 'Preso in carico',
  in_lavorazione: 'In lavorazione',
  in_attesa_associato: 'In attesa di risposta',
  risolto: 'Risolto',
  chiuso: 'Chiuso',
};

/** Etichette lato ospite: si parla di «richiesta», quindi femminile. */
export const GUEST_STATUS_LABELS: Record<string, string> = {
  aperto: 'Aperta',
  preso_in_carico: 'Presa in carico',
  in_lavorazione: 'In lavorazione',
  in_attesa_associato: 'In attesa di risposta',
  risolto: 'Risolta',
  chiuso: 'Chiusa',
};

export const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  aperto: 'info',
  preso_in_carico: 'primary',
  in_lavorazione: 'warning',
  in_attesa_associato: 'secondary',
  risolto: 'success',
  chiuso: 'secondary',
};

export const PRIORITY_LABELS: Record<string, string> = {
  bassa: 'Bassa',
  media: 'Media',
  alta: 'Alta',
  urgente: 'Urgente',
};

export const PRIORITY_VARIANTS: Record<string, BadgeVariant> = {
  bassa: 'secondary',
  media: 'info',
  alta: 'warning',
  urgente: 'danger',
};

/**
 * Transizioni valide: stato corrente -> stati selezionabili.
 * Speculare ad `ALLOWED` di `backend/utils/supportTicket.js`: «Chiuso» NON e
 * selezionabile a mano — un ticket risolto viene chiuso dal cron dopo
 * REOPEN_WINDOW_DAYS giorni, oppure da "Non pertinente: ignora mittente" e dal merge.
 */
export const STATUS_TRANSITIONS: Record<string, string[]> = {
  aperto: ['preso_in_carico', 'in_lavorazione'],
  preso_in_carico: ['in_lavorazione', 'in_attesa_associato', 'risolto'],
  in_lavorazione: ['in_attesa_associato', 'risolto'],
  in_attesa_associato: ['in_lavorazione', 'risolto'],
  risolto: ['in_lavorazione'],
  chiuso: [],
};

/** Azioni di rifinitura testo lato AI (action del backend -> etichetta UI). */
export const AI_ACTIONS: { action: string; label: string }[] = [
  { action: 'migliora', label: 'Migliora' },
  { action: 'correggi', label: 'Correggi' },
  { action: 'formale', label: 'Più formale' },
  { action: 'sintetizza', label: 'Sintetizza' },
];

/**
 * Vincoli allegati: max 5 file da 10MB (middleware/uploadSupport.js).
 * La whitelist dei tipi rispecchia `EXT_BY_MIME` di backend/utils/supportAttachments.js
 * (PDF, immagini e documenti Office): tenerle allineate, altrimenti il client
 * rifiuta file che il server accetterebbe — o viceversa.
 */
export const MAX_FILES = 5;
export const MAX_FILE_SIZE = 10 * 1024 * 1024;
export const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
export const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,image/webp';

/** Finestra di riapertura, speculare a `backend/utils/supportTicket.canReopen`. */
export const REOPEN_WINDOW_DAYS = 14;

export type ComposeTarget = 'reply' | 'note';

/** Data estesa usata nel dettaglio operatore. */
export const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Data breve usata nelle pagine richiedente. */
export const formatShortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Iniziali per gli avatar: `nome` dal backend è già il nominativo intero. */
export const initialsOf = (nome?: string, cognome?: string) => {
  const parts = `${nome || ''} ${cognome || ''}`.trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]).join('');
  return initials.toUpperCase() || '–';
};
